import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, Clock, Globe2 } from "lucide-react";
import { SERVICE_CATEGORIES } from "@/data/services";

const LOGO_URL = "/logo.png";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";

  return (
    <footer className="bg-white border-t border-slate-200 mt-12" data-testid="site-footer">
      <div className="container-x py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BIONET SOLUTIONS" className="h-12 w-auto object-contain" />
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-brand-green text-lg">BIONET SOLUTIONS</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Nettoyage &amp; Services
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">{t("footer.tagline")}</p>
        </div>

        <div>
          <h4 className="font-heading font-bold text-slate-900 mb-4">{t("footer.services_col")}</h4>
          <ul className="space-y-2 text-sm">
            {SERVICE_CATEGORIES.slice(0, 7).map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/services/${c.slug}`}
                  className="text-slate-600 hover:text-brand-green transition"
                  data-testid={`footer-service-${c.slug}`}
                >
                  {t(`categories.${c.slug}.name`)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold text-slate-900 mb-4">{t("footer.company_col")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/services" className="text-slate-600 hover:text-brand-green">{t("nav.services")}</Link></li>
            <li><Link to="/devis" className="text-slate-600 hover:text-brand-green">{t("nav.quote")}</Link></li>
            <li><Link to="/blog" className="text-slate-600 hover:text-brand-green">{t("nav.blog")}</Link></li>
            <li><Link to="/partenaires" className="text-slate-600 hover:text-brand-green">{t("nav.partners")}</Link></li>
            <li><Link to="/a-propos" className="text-slate-600 hover:text-brand-green">{t("nav.about")}</Link></li>
            <li><Link to="/faq" className="text-slate-600 hover:text-brand-green">{t("nav.faq")}</Link></li>
            <li><Link to="/contact" className="text-slate-600 hover:text-brand-green">{t("nav.contact")}</Link></li>
            <li><Link to="/mentions-legales" className="text-slate-600 hover:text-brand-green">{t("footer.legal_notice")}</Link></li>
            <li><Link to="/politique-confidentialite" className="text-slate-600 hover:text-brand-green">{t("footer.privacy")}</Link></li>
            <li><Link to="/cgv" className="text-slate-600 hover:text-brand-green">{t("footer.cgv")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-heading font-bold text-slate-900 mb-4">{t("footer.contact_col")}</h4>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 text-brand-blue mt-0.5" />
              <a href="tel:+33649502801" className="hover:text-brand-green" data-testid="footer-phone">
                06 49 50 28 01
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 text-brand-blue mt-0.5" />
              <a href="mailto:contact@bionet-solutions.com" className="hover:text-brand-green break-all" data-testid="footer-email">
                contact@bionet-solutions.com
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-brand-blue mt-0.5" />
              <span>64 chemin de Sermenaz<br />01700 Neyron</span>
            </li>
            <li className="flex items-start gap-2">
              <Globe2 className="h-4 w-4 text-brand-blue mt-0.5" />
              <span>{t("common.country")}</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-brand-blue mt-0.5" />
              <span>{t("common.hours")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-200">
        <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} BIONET SOLUTIONS. {t("footer.rights")}</div>
          <div className="flex items-center gap-4">
            <span>{lang.toUpperCase()}</span>
            <span>·</span>
            <span>SIRET 944 251 479 00019 -SAS -APE 8121Z</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
